import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import DeletePopUp from "../DeletePopUp";
import { darkenColor } from "../../exports/functions";
import { GripVertical, Pencil } from "lucide-react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  ICategory,
  IModules,
  IStatusManagementOption,
} from "../../exports/interfaces";
import { usePermission } from "../../hooks/usePermission";
import { useLoader } from "../../context/LoaderContext";
import { useOption } from "../../context/OptionContext";
import axiosClient from "../../axioxClient.ts/axiosClient";
import {
  fetchDropdownCategories,
  fetchStatusManagementOption,
} from "../../api/AdminApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useLocation } from "react-router-dom";
import {
  findMatchingOptionColor,
  syncMatchingOptionColors,
} from "../../exports/optionColorSync";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

export const getStatusClass = (status: string) => {
  switch (status) {
    case "Active":
      return "pill-success";
    case "Inactive":
      return "pill-danger";
    case "Deactivated":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
};

// const categories = [
//   "Cooperative Statuses",
//   "Complaint Statuses",
//   "Notice Statuses",
//   "User Statuses",
// ];

// const rows = [
//   {
//     id: 1,
//     label: "Agriculture",
//     value: "Agriculture Cooperative",
//     color: "#B5CAE5",
//     order: 1,
//     status: "Active",
//   },
//   {
//     id: 2,
//     label: "Multipurpose",
//     value: "Multipurpose Cooperative",
//     color: "#EFAE6D",
//     order: 2,
//     status: "Active",
//   },
//   {
//     id: 3,
//     label: "Saving and Credits",
//     value: "Saving and Credits Cooperative",
//     color: "#C2DD9F",
//     order: 3,
//     status: "Active",
//   },
//   {
//     id: 4,
//     label: "Other",
//     value: "Other Cooperative",
//     color: "#5C5C5C",
//     order: 4,
//     status: "Active",
//   },
// ];

const StatusManagement = ({
  statusOptions,
  setStatusOptions,
  categories: propCategories,
  setCategories: propSetCategories,
}: {
  statusOptions: IModules[];
  setStatusOptions: Dispatch<SetStateAction<IModules[]>>;
  categories?: ICategory[];
  setCategories?: Dispatch<SetStateAction<ICategory[]>>;
}) => {
  const { setLoading } = useLoader();
  const { canCreate, canEdit, canDelete } = usePermission();
  const location = useLocation();
  const { user } = useUser();
  const { sendNotification } = useNotification();
  const sectionId = location.state?.sectionId;

  const { categories: contextCategories, setCategories: contextSetCategories } =
    useOption();
  const categories = propCategories || contextCategories || [];
  const setCategories = propSetCategories || contextSetCategories;

  const [selectedModule, setSelectedModule] = useState<IModules | null>(null);
  const [tableRows, setTableRows] = useState<IStatusManagementOption[]>([]);
  const [showDelete, setShowDelete] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [newLabel, setNewLabel] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newColor, setNewColor] = useState("");
  const [newOrder, setNewOrder] = useState<string>("");
  const [newStatus, setNewStatus] = useState("Active");

  const [newErrors, setNewErrors] = useState<{
    label: string;
    value: string;
    color: string;
    order: string;
  }>({
    label: "",
    value: "",
    color: "",
    order: "",
  });
  const [editErrors, setEditErrors] = useState<{
    label: string;
    value: string;
    color: string;
  }>({
    label: "",
    value: "",
    color: "",
  });

  // Track if we've initialized selectedCategory
  const initialized = useRef(false);

  const handleCategorySelect = (module: IModules) => {
    setSelectedModule(module);
    const sorted = [...(module.statusOptions || [])].sort(
      (a, b) => Number(a.order || 0) - Number(b.order || 0),
    );
    setTableRows(sorted);
    const maxOrder = sorted.length
      ? Math.max(...sorted.map((d) => Number(d.order || 0)))
      : 0;
    setNewOrder(String(maxOrder + 1));
  };

  useEffect(() => {
    if (!initialized.current && statusOptions.length > 0) {
      initialized.current = true;

      const selected =
        statusOptions.find((item) => item.name === sectionId) ||
        statusOptions[0];

      setSelectedModule(selected);
      const sorted = [...(selected.statusOptions || [])].sort(
        (a, b) => Number(a.order || 0) - Number(b.order || 0),
      );
      setTableRows(sorted);

      const maxOrder = sorted.length
        ? Math.max(...sorted.map((d) => Number(d.order || 0)))
        : 0;

      setNewOrder(String(maxOrder + 1));
    }
  }, [statusOptions, sectionId]);

  const getDropdownErrors = (
    label: string,
    value: string,
    color?: string,
    order?: string,
  ) => {
    const errors = { label: "", value: "", color: "", order: "" };

    if (!label.trim()) {
      errors.label = "Label is required.";
    }

    if (!value.trim()) {
      errors.value = "Value is required.";
    }

    if (color !== undefined) {
      if (!color.trim()) {
        errors.color = "Color is required.";
      }
    }

    if (order !== undefined) {
      if (!order.trim()) {
        errors.order = "Sort order is required.";
      } else {
        const orderNumber = Number(order);
        if (Number.isNaN(orderNumber) || orderNumber < 0) {
          errors.order = "Sort order must be a non-negative number.";
        }
      }
    }

    return errors;
  };

  const validateDropdownFields = (
    label: string,
    value: string,
    color?: string,
    order?: string,
    setErrors?: boolean,
  ): boolean => {
    const errors = getDropdownErrors(label, value, color, order);

    if (setErrors) {
      setNewErrors(errors);
    }

    if (errors.label || errors.value || errors.color || errors.order) {
      if (!setErrors) {
        dynamicFailed(
          errors.label || errors.value || errors.color || errors.order,
        );
      }
      return false;
    }

    return true;
  };

  const handleClose = () => {
    setShowDelete(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setLoading(true);
    try {
      const res = await axiosClient.delete(
        `/options/status-option/${deleteId}`,
      );

      if (res.status === 200 || res.status === 201) {
        const modules = await fetchStatusManagementOption();
        setStatusOptions(modules);
        const updated =
          modules.find((c) => c.id === selectedModule?.id) || null;
        setSelectedModule(updated);
        setTableRows(
          [...(updated?.statusOptions || [])].sort(
            (a, b) => Number(a.order || 0) - Number(b.order || 0),
          ),
        );
        sendNotification(
          `${selectedModule?.name} status deleted`,
          user?.user?.userName || "User",
        );
        dynamicSuccess("Status deleted successfully!");
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Something went wrong!";
      dynamicFailed(message);
    } finally {
      setTimeout(() => setLoading(false), 1000);
      setShowDelete(false);
      setDeleteId(null);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    const previousRows = [...tableRows];

    // Reorder items in array
    const reordered = Array.from(tableRows);
    const [movedItem] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, movedItem);

    // Update order numbers (1-indexed)
    const updatedRows = reordered.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));

    // Optimistically update table UI
    setTableRows(updatedRows);

    // Identify changed items to update
    const changedItems = updatedRows.filter((item) => {
      const original = previousRows.find((r) => r.id === item.id);
      return original && Number(original.order) !== Number(item.order);
    });

    if (changedItems.length === 0) return;

    try {
      setLoading(true);

      // Persist the updated order for changed status options
      await Promise.all(
        changedItems.map((item) =>
          axiosClient.put(`/options/status-option/${item.id}`, {
            order: item.order,
          }),
        ),
      );

      const modules = await fetchStatusManagementOption();
      setStatusOptions(modules);
      const updated = modules.find((c) => c.id === selectedModule?.id) || null;
      setSelectedModule(updated);
      setTableRows(
        [...(updated?.statusOptions || [])].sort(
          (a, b) => Number(a.order || 0) - Number(b.order || 0),
        ),
      );
      setNewOrder(String(updatedRows.length + 1));
      sendNotification(
        `${selectedModule?.name} order updated`,
        user?.user?.userName || "User",
      );
      dynamicSuccess("Order updated successfully!");
    } catch (error: any) {
      setTableRows(previousRows);
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Failed to update order";

      dynamicFailed(message);
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleSaveEdit = async (id: number) => {
    const row = tableRows.find((r) => r.id === id);
    if (!row) return;

    const errors = getDropdownErrors(row.label, row.value, row.color);
    if (errors.label || errors.value || errors.color) {
      setEditErrors({
        label: errors.label,
        value: errors.value,
        color: errors.color,
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        label: row.label,
        value: row.value,
        color: row.color,
        order: row.order,
        status: row.status,
      };

      const res = await axiosClient.put(
        `/options/status-option/${id}`,
        payload,
      );

      if (res.status === 200 || res.status === 201) {
        await syncMatchingOptionColors({
          target: { label: row.label, value: row.value, color: row.color },
          statusOptions,
          categories,
          exclude: { id, type: "status" },
        });

        const [modules, categoryData] = await Promise.all([
          fetchStatusManagementOption(),
          fetchDropdownCategories(),
        ]);
        setStatusOptions(modules);
        if (setCategories) {
          setCategories(categoryData);
        }
        const updated =
          modules.find((c) => c.id === selectedModule?.id) || null;
        setSelectedModule(updated);
        setTableRows(
          [...(updated?.statusOptions || [])].sort(
            (a, b) => Number(a.order || 0) - Number(b.order || 0),
          ),
        );
        sendNotification(
          `${selectedModule?.name} status updated`,
          user?.user?.userName || "User",
        );
        setEditingId(null);
        dynamicSuccess("Status updated successfully!");
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleSubmit = async () => {
    if (!selectedModule) {
      dynamicFailed("Please select a module first!");
      return;
    }

    if (!validateDropdownFields(newLabel, newValue, newColor, newOrder, true)) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        label: newLabel,
        value: newValue,
        color: newColor,
        order: newOrder !== "" ? Number(newOrder) : undefined,
        status: newStatus,
        moduleId: selectedModule.id,
      };

      const res = await axiosClient.post("/options/status-option", payload);

      if (res.status === 200 || res.status === 201) {
        await syncMatchingOptionColors({
          target: { label: newLabel, value: newValue, color: newColor },
          statusOptions,
          categories,
        });

        const [modules, categoryData] = await Promise.all([
          fetchStatusManagementOption(),
          fetchDropdownCategories(),
        ]);
        setStatusOptions(modules);
        if (setCategories) {
          setCategories(categoryData);
        }
        const updated = modules.find((c) => c.id === selectedModule.id) || null;
        setSelectedModule(updated);
        setTableRows(
          [...(updated?.statusOptions || [])].sort(
            (a, b) => Number(a.order || 0) - Number(b.order || 0),
          ),
        );

        setNewLabel("");
        setNewValue("");
        setNewColor("");
        setNewOrder("");
        setNewStatus("Active");
        setShowForm(false);
        sendNotification(
          `${selectedModule?.name} status added`,
          user?.user?.userName || "User",
        );
        dynamicSuccess("Status added successfully!");
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleRowChange = (
    id: number,
    field: "label" | "value" | "color",
    value: string,
  ) => {
    setTableRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const updated = { ...row, [field]: value };
        if (field === "label" || field === "value") {
          const matchedColor = findMatchingOptionColor(
            { label: updated.label, value: updated.value },
            statusOptions,
            categories,
            { id: row.id, type: "status" },
          );
          if (matchedColor) {
            updated.color = matchedColor;
          }
        }
        return updated;
      }),
    );
  };

  return (
    <>
      <div className="row g-3">
        {/* Left Sidebar */}
        <div className="col-lg-3 col-md-5">
          <div className="dashboard-card h-100 p-0 overflow-hidden">
            <h5 className="primary-color p-2 border-bottom mb-0">Modules</h5>

            <div className="list-group list-group-flush custom-list-group">
              {statusOptions.map((option) => (
                <button
                  key={option.id}
                  className={`list-group-item list-group-item-action border-0 border-bottom text-start ${
                    selectedModule?.id === option.id ? "active-category" : ""
                  }`}
                  onClick={() => handleCategorySelect(option)}
                >
                  {option.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="col-lg-9 col-md-7">
          <div id={selectedModule?.name} className="dashboard-card">
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
              <div>
                <h5 className="primary-color">{selectedModule?.name}</h5>
                <small>Manage available options for this dropdown field</small>
              </div>

              {canCreate("Admin Control") && (
                <button
                  className="btn btn-badge-primary px-3"
                  onClick={() => {
                    if (selectedModule) {
                      const maxOrder =
                        selectedModule.statusOptions &&
                        selectedModule.statusOptions.length
                          ? Math.max(
                              ...selectedModule.statusOptions.map((o) =>
                                Number(o.order || 0),
                              ),
                            )
                          : 0;
                      setNewOrder(String(maxOrder + 1));
                    }
                    setShowForm((prev) => !prev);
                  }}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Add Option
                </button>
              )}
            </div>
            <div
              className={`option-form-wrapper dashboard-card ${showForm ? "open" : "p-0"}`}
            >
              <div className="option-form">
                <div className="row g-3">
                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Value (Slug)</label>
                    <input
                      type="text"
                      className={`form-control ${newErrors.value ? "input-error" : ""}`}
                      placeholder="e.g Agriculture"
                      value={newValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewValue(val);
                        const matched = findMatchingOptionColor(
                          { label: newLabel, value: val },
                          statusOptions,
                          categories,
                        );
                        if (matched) {
                          setNewColor(matched);
                          if (newErrors.color) {
                            setNewErrors((prev) => ({ ...prev, color: "" }));
                          }
                        }
                        if (newErrors.value) {
                          setNewErrors((prev) => ({ ...prev, value: "" }));
                        }
                      }}
                    />
                    {newErrors.value && (
                      <div className="text-error">{newErrors.value}</div>
                    )}
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Label (Display)</label>
                    <input
                      type="text"
                      className={`form-control ${newErrors.label ? "input-error" : ""}`}
                      placeholder="e.g Agriculture"
                      value={newLabel}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewLabel(val);
                        const matched = findMatchingOptionColor(
                          { label: val, value: newValue },
                          statusOptions,
                          categories,
                        );
                        if (matched) {
                          setNewColor(matched);
                          if (newErrors.color) {
                            setNewErrors((prev) => ({ ...prev, color: "" }));
                          }
                        }
                        if (newErrors.label) {
                          setNewErrors((prev) => ({ ...prev, label: "" }));
                        }
                      }}
                    />
                    {newErrors.label && (
                      <div className="text-error">{newErrors.label}</div>
                    )}
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Color</label>
                    <div className="color-input-wrapper">
                      <input
                        type="color"
                        value={newColor}
                        onChange={(e) => {
                          setNewColor(e.target.value);
                          if (newErrors.color) {
                            setNewErrors((prev) => ({ ...prev, color: "" }));
                          }
                        }}
                        className="color-circle"
                      />

                      <input
                        type="text"
                        value={newColor.toUpperCase()}
                        onChange={(e) => {
                          setNewColor(e.target.value);
                          if (newErrors.color) {
                            setNewErrors((prev) => ({ ...prev, color: "" }));
                          }
                        }}
                        className={`form-control color-text-input ${newErrors.color ? "input-error" : ""}`}
                        placeholder="e.g #B5CAE5"
                      />
                    </div>
                    {newErrors.color && (
                      <div className="text-error">{newErrors.color}</div>
                    )}
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Sort Order</label>
                    <input
                      type="number"
                      min={0}
                      className={`form-control ${newErrors.order ? "input-error" : ""}`}
                      value={newOrder}
                      onChange={(e) => {
                        setNewOrder(e.target.value);
                        if (newErrors.order) {
                          setNewErrors((prev) => ({ ...prev, order: "" }));
                        }
                      }}
                    />
                    {newErrors.order && (
                      <div className="text-error">{newErrors.order}</div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    className="btn btn-badge-border px-3"
                    onClick={() => {
                      setShowForm(false);
                      setNewLabel("");
                      setNewValue("");
                      setNewColor("");
                      setNewOrder("");
                      setNewStatus("Active");
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleSubmit}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="table-responsive admin-table table-checkbox status-fix-column">
              <DragDropContext onDragEnd={handleDragEnd}>
                <table className="table align-middle">
                  <thead className="table-light">
                    <tr>
                      <th className="table-xs"></th>
                      <th className="table-md">Label</th>
                      <th className="table-md">Value</th>
                      <th className="table-sm">Color</th>
                      <th className="table-md">Status</th>
                      <th className="table-xs">Order</th>
                      <th className="table-sm">Action</th>
                    </tr>
                  </thead>

                  <Droppable droppableId="status-management-droppable">
                    {(provided) => (
                      <tbody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {tableRows.map((row, index) => (
                          <Draggable
                            key={String(row.id)}
                            draggableId={String(row.id)}
                            index={index}
                            isDragDisabled={
                              !canEdit("Admin Control") || editingId !== null
                            }
                          >
                            {(dragProvided, snapshot) => (
                              <tr
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                style={{
                                  ...dragProvided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging
                                    ? "var(--light, #f8f9fa)"
                                    : undefined,
                                  boxShadow: snapshot.isDragging
                                    ? "0 4px 12px rgba(0, 0, 0, 0.15)"
                                    : undefined,
                                }}
                              >
                                <td
                                  {...dragProvided.dragHandleProps}
                                  className="table-xs text-center"
                                  style={{
                                    cursor:
                                      canEdit("Admin Control") &&
                                      editingId === null
                                        ? "grab"
                                        : "default",
                                  }}
                                  title={
                                    canEdit("Admin Control") &&
                                    editingId === null
                                      ? "Drag to reorder"
                                      : undefined
                                  }
                                >
                                  <GripVertical
                                    size={16}
                                    className={
                                      canEdit("Admin Control") &&
                                      editingId === null
                                        ? "text-secondary"
                                        : "text-muted opacity-50"
                                    }
                                  />
                                </td>
                                <td className="table-md">
                                  {editingId === row.id ? (
                                    <>
                                      <input
                                        type="text"
                                        className={`form-control ${editErrors.label ? "input-error" : ""}`}
                                        value={row.label}
                                        onChange={(e) => {
                                          handleRowChange(
                                            row.id,
                                            "label",
                                            e.target.value,
                                          );
                                          if (editErrors.label) {
                                            setEditErrors((prev) => ({
                                              ...prev,
                                              label: "",
                                            }));
                                          }
                                        }}
                                      />
                                      {editErrors.label && (
                                        <div className="text-error">
                                          {editErrors.label}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    row.label
                                  )}
                                </td>
                                <td className="table-md">
                                  {editingId === row.id ? (
                                    <>
                                      <input
                                        type="text"
                                        className={`form-control ${editErrors.value ? "input-error" : ""}`}
                                        value={row.value}
                                        onChange={(e) => {
                                          handleRowChange(
                                            row.id,
                                            "value",
                                            e.target.value,
                                          );
                                          if (editErrors.value) {
                                            setEditErrors((prev) => ({
                                              ...prev,
                                              value: "",
                                            }));
                                          }
                                        }}
                                      />
                                      {editErrors.value && (
                                        <div className="text-error">
                                          {editErrors.value}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    row.value
                                  )}
                                </td>
                                <td className="table-sm">
                                  {editingId === row.id ? (
                                    <div className="color-input-wrapper">
                                      <input
                                        type="color"
                                        value={row.color}
                                        onChange={(e) =>
                                          handleRowChange(
                                            row.id,
                                            "color",
                                            e.target.value,
                                          )
                                        }
                                        className="color-circle"
                                        style={
                                          editErrors.color ? { top: "35%" } : {}
                                        }
                                      />

                                      <input
                                        type="text"
                                        value={row.color.toUpperCase()}
                                        onChange={(e) =>
                                          handleRowChange(
                                            row.id,
                                            "color",
                                            e.target.value,
                                          )
                                        }
                                        className={`form-control color-text-input ${editErrors.color ? "input-error" : ""}`}
                                      />
                                      {editErrors.color && (
                                        <div className="text-error">
                                          {editErrors.color}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          backgroundColor: row.color,
                                          borderRadius: "50%",
                                          display: "inline-block",
                                          border: "1px solid #ddd",
                                        }}
                                      />
                                      <small>{row.color}</small>
                                    </div>
                                  )}
                                </td>
                                <td className="table-md">
                                  <span
                                    className={`badge rounded-pill px-3 py-2`}
                                    style={{
                                      backgroundColor: row.color,
                                      color: darkenColor(row.color, 40),
                                    }}
                                  >
                                    {row.label}
                                  </span>
                                </td>
                                <td className="table-xs">{row.order}</td>

                                <td className="table-sm">
                                  <div className="d-flex gap-3 align-items-center">
                                    {editingId === row.id ? (
                                      <>
                                        <i
                                          className={`fa-solid fa-floppy-disk text-success ${
                                            canEdit("Admin Control")
                                              ? "cursor-pointer"
                                              : "opacity-50"
                                          }`}
                                          title={
                                            canEdit("Admin Control")
                                              ? "Save"
                                              : "No permission"
                                          }
                                          style={{
                                            pointerEvents: canEdit(
                                              "Admin Control",
                                            )
                                              ? "auto"
                                              : "none",
                                          }}
                                          onClick={() => {
                                            if (!canEdit("Admin Control"))
                                              return;
                                            handleSaveEdit(row.id);
                                          }}
                                        />

                                        <i
                                          className={`fa-solid fa-trash text-danger ${
                                            canDelete("Admin Control")
                                              ? "cursor-pointer"
                                              : "opacity-50"
                                          }`}
                                          title={
                                            canDelete("Admin Control")
                                              ? "Delete"
                                              : "No permission"
                                          }
                                          style={{
                                            pointerEvents: canDelete(
                                              "Admin Control",
                                            )
                                              ? "auto"
                                              : "none",
                                          }}
                                          onClick={() => {
                                            if (!canDelete("Admin Control"))
                                              return;
                                            setDeleteId(row?.id || 0);
                                            setShowDelete(true);
                                          }}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className={`cursor-pointer ${
                                            canEdit("Admin Control")
                                              ? ""
                                              : "opacity-50"
                                          }`}
                                          title={
                                            canEdit("Admin Control")
                                              ? "Edit"
                                              : "No permission"
                                          }
                                          style={{
                                            pointerEvents: canEdit(
                                              "Admin Control",
                                            )
                                              ? "auto"
                                              : "none",
                                          }}
                                          onClick={() => {
                                            if (!canEdit("Admin Control"))
                                              return;
                                            setEditingId(row.id);
                                          }}
                                        >
                                          <Pencil size={25} />
                                        </i>
                                        <i
                                          className={`fa-solid fa-trash text-danger ${
                                            canDelete("Admin Control")
                                              ? "cursor-pointer"
                                              : "opacity-50"
                                          }`}
                                          title={
                                            canDelete("Admin Control")
                                              ? "Delete"
                                              : "No permission"
                                          }
                                          style={{
                                            pointerEvents: canDelete(
                                              "Admin Control",
                                            )
                                              ? "auto"
                                              : "none",
                                          }}
                                          onClick={() => {
                                            if (!canDelete("Admin Control"))
                                              return;
                                            setDeleteId(row?.id || 0);
                                            setShowDelete(true);
                                          }}
                                        />
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </table>
              </DragDropContext>
            </div>
          </div>
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default StatusManagement;
